import React, { useState } from "react";
import { MapPin, Loader2, Send } from "lucide-react";
import { useCity } from "../context/CityContext";
import { useI18n } from "../context/I18nContext";
import { BagLogo } from "./Logo";

// Shown only when no city is known yet (new account or fresh browser). One-time location
// request, explicit user action, and a manual fallback — never an automatic default city.
export function CityOnboarding() {
  const { cities, needsCity, selectCity, detectCityByGeolocation } = useCity();
  const { t } = useI18n();
  const [state, setState] = useState("intro"); // intro | detecting | failed | outside
  const [manual, setManual] = useState(false);

  if (!needsCity) return null;

  const allow = async () => {
    setState("detecting");
    const res = await detectCityByGeolocation();
    if (res.status === "OK") return; // needsCity flips to false and the sheet closes
    setState(res.status === "OUTSIDE" ? "outside" : "failed");
    setManual(true);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-brand-red flex items-center justify-center p-5" data-testid="city-onboarding">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 text-center">
        <div className="flex justify-center mb-3"><BagLogo size={48} /></div>

        {state === "detecting" ? (
          <>
            <Loader2 size={28} className="mx-auto text-brand-red animate-spin" />
            <p data-testid="city-detecting" className="mt-3 font-display font-black text-lg">
              {t("city.detecting", "Određujemo vaš grad…")}
            </p>
          </>
        ) : (
          <>
            <h2 className="font-display font-black text-xl">
              {state === "intro"
                ? t("city.allow_title", "Dozvoli pristup lokaciji")
                : state === "outside"
                  ? t("city.unsupported", "Ponesi trenutno još nije dostupan u vašem gradu.")
                  : t("city.failed", "Nismo mogli automatski da odredimo vaš grad.")}
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              {state === "intro"
                ? t("city.allow_body", "Ponesi koristi vašu lokaciju da automatski odredi grad u kojem se trenutno nalazite.")
                : t("city.choose_manual", "Izaberite grad ručno.")}
            </p>

            {state === "intro" && (
              <button data-testid="city-allow-btn" onClick={allow}
                className="mt-5 w-full rounded-2xl bg-brand-red text-white py-4 font-display font-black flex items-center justify-center gap-2">
                <MapPin size={18} />{t("city.allow_btn", "Dozvoli lokaciju")}
              </button>
            )}

            {(manual || state !== "intro") && (
              <div className="mt-4 space-y-2" data-testid="city-manual-list">
                {cities.map((c) => (
                  <button key={c.id} data-testid={`city-onboarding-${c.id}`} onClick={() => selectCity(c.id)}
                    className="w-full flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-3.5 font-bold text-slate-800">
                    <Send size={15} className="-rotate-12 text-brand-red" />{c.name}
                  </button>
                ))}
              </div>
            )}

            {state === "intro" && (
              <button data-testid="city-manual-btn" onClick={() => setManual(true)}
                className="mt-3 text-sm font-bold text-slate-400">
                {t("city.manual_link", "Izaberi grad ručno")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
